/**
 * Agent Ecosystem View Component
 * Educational agent ecosystem overview
 */

import React from 'react';
import { 
  BookOpen, 
  ClipboardCheck, 
  MessageSquare, 
  Target, 
  CheckCircle,
  Users,
  BarChart,
  TrendingUp,
  Brain,
  Network
} from 'lucide-react';

const AgentEcosystemView = () => {
  const agents = [
    {
      name: "Teaching Delivery Agent",
      role: "Manages lesson content delivery and teaching materials",
      icon: <BookOpen className="w-5 h-5" />,
      color: "bg-blue-500",
      responsibilities: [
        "Deliver lesson content",
        "Organize teaching materials",
        "Create interactive presentations",
        "Manage classroom activities"
      ],
      outputs: [
        "Lesson presentations",
        "Teaching materials",
        "Interactive content",
        "Classroom resources"
      ]
    },
    {
      name: "Assessment Agent",
      role: "Creates and grades exams, quizzes, and tests",
      icon: <ClipboardCheck className="w-5 h-5" />,
      color: "bg-purple-500",
      responsibilities: [
        "Generate assessments",
        "Administer tests/quizzes",
        "Grade submissions",
        "Analyze performance data"
      ],
      outputs: [
        "Test results",
        "Score distributions",
        "Performance analytics",
        "Assessment reports"
      ]
    },
    {
      name: "Feedback Agent",
      role: "Generates personalized feedback for students",
      icon: <MessageSquare className="w-5 h-5" />,
      color: "bg-green-500",
      responsibilities: [
        "Analyze results",
        "Generate feedback",
        "Identify improvement areas",
        "Track progress patterns"
      ],
      outputs: [
        "Personalized feedback",
        "Improvement areas",
        "Progress reports",
        "Recommendations"
      ]
    },
    {
      name: "Adaptive Learning Agent",
      role: "Sets personalized learning goals for each student",
      icon: <Target className="w-5 h-5" />,
      color: "bg-orange-500",
      responsibilities: [
        "Analyze performance gaps",
        "Create learning goals",
        "Adapt difficulty levels",
        "Track goal achievement"
      ],
      outputs: [
        "Personalized goals",
        "Learning paths",
        "Adaptive content",
        "Progress tracking"
      ]
    },
    {
      name: "Learning Analytics Agent",
      role: "Analyzes student performance and learning patterns",
      icon: <BarChart className="w-5 h-5" />,
      color: "bg-blue-500",
      responsibilities: [
        "Track student progress",
        "Analyze performance data",
        "Identify learning gaps",
        "Generate insights"
      ],
      outputs: [
        "Performance reports",
        "Learning patterns",
        "Gap analysis",
        "Recommendations"
      ]
    },
    {
      name: "Content Generation Agent",
      role: "Creates personalized learning materials",
      icon: <BookOpen className="w-5 h-5" />,
      color: "bg-purple-500",
      responsibilities: [
        "Generate study materials",
        "Create practice questions",
        "Adapt content difficulty",
        "Curate resources"
      ],
      outputs: [
        "Custom lessons",
        "Practice exercises",
        "Study guides",
        "Video recommendations"
      ]
    },
    {
      name: "Intervention Agent",
      role: "Identifies struggling students and triggers support",
      icon: <Users className="w-5 h-5" />,
      color: "bg-red-500",
      responsibilities: [
        "Monitor early warnings",
        "Detect struggles",
        "Recommend support",
        "Alert teachers"
      ],
      outputs: [
        "Risk alerts",
        "Support requests",
        "Intervention plans",
        "Teacher notifications"
      ]
    },
    {
      name: "Coordination Agent",
      role: "Orchestrates the educational workflow",
      icon: <Network className="w-5 h-5" />,
      color: "bg-indigo-500",
      responsibilities: [
        "Manage workflow",
        "Route data between agents",
        "Ensure synchronization",
        "Monitor system health"
      ],
      outputs: [
        "System status",
        "Process reports",
        "Error handling",
        "Analytics dashboard"
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">The Educational Agent Ecosystem</h2>
        <p className="text-gray-600">
          Eight specialized agents work together to manage the complete educational lifecycle. Each agent has specific responsibilities and communicates with other agents to create a seamless learning experience.
        </p>
      </div>

      {/* Agent Cards */}
      <div className="grid grid-cols-1 gap-6">
        {agents.map((agent, index) => (
          <div 
            key={index} 
            className="bg-white p-6 rounded-xl shadow-sm border-2 border-gray-200 hover:border-blue-300 transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className={`${agent.color} p-4 rounded-xl text-white`}>
                  {agent.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{agent.name}</h3>
                  <p className="text-gray-600 mb-4">{agent.role}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Responsibilities:</h4>
                      <ul className="space-y-1">
                        {agent.responsibilities.map((resp, idx) => (
                          <li key={idx} className="text-sm text-gray-600 flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                            <span>{resp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Output Products:</h4>
                      <ul className="space-y-1">
                        {agent.outputs.map((output, idx) => (
                          <li key={idx} className="text-sm text-gray-600 flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span>{output}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Agent Collaboration Example */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-6 rounded-xl text-white">
        <h3 className="text-xl font-bold mb-3">Agent Collaboration Example</h3>
        <div className="space-y-3 text-blue-50">
          <p className="flex items-start space-x-2">
            <span className="font-bold">1.</span>
            <span><strong>Learning Analytics Agent</strong> detects that a student struggles with quadratic equations (60% accuracy)</span>
          </p>
          <p className="flex items-start space-x-2">
            <span className="font-bold">2.</span>
            <span><strong>Content Generation Agent</strong> creates personalized practice problems at appropriate difficulty level</span>
          </p>
          <p className="flex items-start space-x-2">
            <span className="font-bold">3.</span>
            <span><strong>Assessment Agent</strong> evaluates the student's work and provides detailed feedback on mistakes</span>
          </p>
          <p className="flex items-start space-x-2">
            <span className="font-bold">4.</span>
            <span><strong>Intervention Agent</strong> monitors progress and alerts the teacher if the student needs human support</span>
          </p>
          <p className="flex items-start space-x-2">
            <span className="font-bold">5.</span>
            <span><strong>Coordination Agent</strong> ensures all agents work in sync and data flows smoothly between them</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentEcosystemView;
